import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import {
  HiHome,
  HiShoppingCart,
  HiCube,
  HiDocumentText,
  HiChartBar,
  HiCurrencyDollar,
  HiLogout,
  HiShieldCheck,
  HiCog
} from 'react-icons/hi';
import { logout, getApiBaseUrl, getShopInquiryCount } from '../utils/api';
import AdminMobileNav from './AdminMobileNav';

const PENDING_INQUIRY_POLL_MS = 45000;

const AppLayout = ({ children }) => {
  const [hasLoginHistoryAccess, setHasLoginHistoryAccess] = useState(false);
  const [pendingInquiryCount, setPendingInquiryCount] = useState(0);
  const location = useLocation();
  const previousCountRef = useRef(null);

  useEffect(() => {
    const checkLoginHistoryAccess = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/auth/login-history?page=1&limit=1`, {
          credentials: 'include',
        });
        setHasLoginHistoryAccess(response.ok);
      } catch (error) {
        setHasLoginHistoryAccess(false);
      }
    };

    checkLoginHistoryAccess();
  }, []);

  const loadPendingInquiries = useCallback(async ({ notifyOnIncrease = true } = {}) => {
    try {
      const data = await getShopInquiryCount('pending');
      const nextCount = data.count || 0;
      const prev = previousCountRef.current;
      if (notifyOnIncrease && prev !== null && nextCount > prev) {
        const added = nextCount - prev;
        toast(
          added === 1
            ? '1 new shop inquiry pending'
            : `${added} new shop inquiries pending (${nextCount} total)`,
          { id: 'pending-shop-inquiries', duration: 4000 }
        );
      }
      previousCountRef.current = nextCount;
      setPendingInquiryCount(nextCount);
    } catch {
      setPendingInquiryCount(0);
    }
  }, []);

  useEffect(() => {
    loadPendingInquiries({ notifyOnIncrease: false });

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadPendingInquiries({ notifyOnIncrease: true });
      }
    }, PENDING_INQUIRY_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadPendingInquiries({ notifyOnIncrease: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadPendingInquiries]);

  useEffect(() => {
    loadPendingInquiries({ notifyOnIncrease: false });
  }, [location.pathname, loadPendingInquiries]);

  const handleLogout = async () => {
    try {
      await logout();
      if (window.sessionManager) {
        window.sessionManager.destroy();
      }
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/admin/login';
    }
  };

  const menuItems = [
    {
      path: '/admin',
      label: 'Overview',
      icon: HiHome,
      description: 'Dashboard & quick stats'
    },
    {
      path: '/admin/orders/retail',
      label: 'Retail Order',
      icon: HiShoppingCart,
      description: 'Create retail orders'
    },
    {
      path: '/admin/orders/wholesale',
      label: 'Wholesale Order',
      icon: HiCube,
      description: 'Create wholesale orders'
    },
    {
      path: '/admin/inventory',
      label: 'Inventory',
      icon: HiDocumentText,
      description: 'Manage products'
    },
    {
      path: '/admin/inventory/gallery',
      label: 'Inventory Gallery',
      icon: HiDocumentText,
      description: 'Visual inventory gallery'
    },
    {
      path: '/admin/orders',
      label: 'Order Management',
      icon: HiChartBar,
      description: 'View & manage orders'
    },
    {
      path: '/admin/finance',
      label: 'Finance',
      icon: HiCurrencyDollar,
      description: 'Settlement reports'
    },
    {
      path: '/admin/settings',
      label: 'Settings',
      icon: HiCog,
      description: 'Order numbering & go-live'
    },
    {
      path: '/admin/login-history',
      label: 'Login History',
      icon: HiShieldCheck,
      description: 'User authentication logs'
    }
  ];

  const isActiveRoute = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    if (path === '/admin/orders') {
      return location.pathname === '/admin/orders';
    }
    if (path === '/admin/orders/retail') {
      return location.pathname === '/admin/orders/retail';
    }
    if (path === '/admin/orders/wholesale') {
      return location.pathname === '/admin/orders/wholesale';
    }
    if (path === '/admin/inventory') {
      return location.pathname === '/admin/inventory';
    }
    if (path === '/admin/inventory/gallery') {
      return location.pathname === '/admin/inventory/gallery';
    }
    return location.pathname.startsWith(path);
  };

  const mobilePageTitle = (() => {
    if (location.pathname === '/admin/orders/retail') return 'Retail Order';
    if (location.pathname === '/admin/orders/wholesale') return 'Wholesale Order';
    if (location.pathname === '/admin/orders') return 'Orders';
    if (location.pathname === '/admin/overview') return 'Overview';
    if (location.pathname === '/admin/inventory/gallery') return 'Inventory Gallery';
    if (location.pathname === '/admin/finance') return 'Finance';
    if (location.pathname === '/admin/settings') return 'Settings';
    if (location.pathname === '/admin/login-history') return 'Login History';
    return 'Ekdant Arts';
  })();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Toaster position="top-right" />
      <div className="hidden lg:flex fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-white shadow-none border-r border-gray-200 flex-col">
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Ekdant Arts"
              className="w-8 h-8 rounded"
            />
            <span className="text-lg font-semibold text-gray-900">Ekdant Arts</span>
          </div>
        </div>

        <nav className="mt-8 px-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {menuItems
              .filter((item) => item.path !== '/admin/login-history' || hasLoginHistoryAccess)
              .map((item) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                      ${isActive
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'}`} />
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {item.label}
                        {item.path === '/admin/orders' && pendingInquiryCount > 0 ? (
                          <span className="admin-inquiry-badge admin-inquiry-badge--pulse">
                            {pendingInquiryCount}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </nav>

        <div className="mt-auto p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-700 transition-colors duration-200 group"
          >
            <HiLogout className="w-5 h-5 mr-3 text-gray-400 group-hover:text-red-500" />
            Sign Out
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col h-[100dvh] lg:h-screen min-h-0 min-w-0 overflow-hidden lg:pl-64">
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3 shrink-0">
          <div className="flex items-center justify-center gap-2">
            <img
              src="/logo.png"
              alt=""
              className="w-6 h-6 rounded"
              aria-hidden="true"
            />
            <span className="text-sm font-semibold text-gray-900 truncate">{mobilePageTitle}</span>
          </div>
        </div>

        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto admin-mobile-main">
          {children}
        </main>

        <AdminMobileNav
          pendingInquiryCount={pendingInquiryCount}
          hasLoginHistoryAccess={hasLoginHistoryAccess}
          onLogout={handleLogout}
        />
      </div>
    </div>
  );
};

export default AppLayout;
